/**
 * Storage Error Handler
 * 
 * Automatically detects and handles browser storage errors (IndexedDB, localStorage)
 * Common in development when browser cache becomes corrupted or full.
 * 
 * Handles:
 * - FILE_ERROR_NO_SPACE: Chrome's LevelDB storage full
 * - QuotaExceededError: Storage quota exceeded
 * - IndexedDB corruption errors
 */

import toast from 'react-hot-toast'

export class StorageError extends Error {
  constructor(message: string, public originalError?: Error) {
    super(message)
    this.name = 'StorageError'
  }
}

/**
 * Check if error is a storage-related error
 */
export function isStorageError(error: unknown): boolean {
  if (!error) return false
  
  const errorString = String(error).toLowerCase()
  const messageString = String((error as { message?: unknown }).message ?? '').toLowerCase()
  
  const storageErrorPatterns = [
    'file_error_no_space',
    'quotaexceedederror',
    'quota exceeded',
    'storage full',
    'indexeddb',
    'localstorage',
    'sessionstorage',
    'dom exception 22',
    'ns_error_dom_quota_reached'
  ]
  
  return storageErrorPatterns.some(pattern => 
    errorString.includes(pattern) || messageString.includes(pattern)
  )
}

/**
 * Clear all browser storage
 */
export async function clearAllStorage(): Promise<void> {
  try {
    // 1. Clear IndexedDB
    if ('databases' in indexedDB) {
      const dbs = await indexedDB.databases()
      
      for (const db of dbs) {
        if (db.name) {
          indexedDB.deleteDatabase(db.name)
        }
      }
    }
    
    // 2. Clear localStorage
    try {
      localStorage.clear()
    } catch {
      // Failed to clear localStorage
    }
    
    // 3. Clear sessionStorage
    try {
      sessionStorage.clear()
    } catch {
      // Failed to clear sessionStorage
    }
    
    // 4. Clear Cache API (Service Workers)
    if ('caches' in window) {
      try {
        const cacheNames = await caches.keys()
        
        for (const cacheName of cacheNames) {
          await caches.delete(cacheName)
        }
      } catch {
        // Failed to clear Cache API
      }
    }
  } catch (error) {
    throw new StorageError('Failed to clear browser storage', error as Error)
  }
}

/**
 * Handle storage errors with automatic recovery
 */
export async function handleStorageError(
  error: Error,
  options: {
    autoReload?: boolean
    showNotification?: boolean
  } = {}
): Promise<void> {
  const {
    autoReload = true,
    showNotification = true
  } = options
  
  if (!isStorageError(error)) {
    // Not a storage error, don't handle
    return
  }
  
  try {
    // Clear all storage
    await clearAllStorage()
    
    // Show user notification
    if (showNotification) {
      toast.success(
        'Browser cache cleared successfully. Reloading page...',
        { duration: 3000 }
      )
    }
    
    // Wait a moment for toast to be visible
    await new Promise(resolve => setTimeout(resolve, 1000))
    
    // Reload page to apply changes
    if (autoReload) {
      window.location.reload()
    }
  } catch {
    if (showNotification) {
      toast.error(
        'Failed to clear cache automatically. Please clear browser cache manually (DevTools > Application > Clear storage)',
        { duration: 8000 }
      )
    }
  }
}

/**
 * Global error handler wrapper - catches unhandled storage errors
 */
export function installGlobalStorageErrorHandler(): void {
  // Handle window errors
  const originalErrorHandler = window.onerror
  window.onerror = function(message, source, lineno, colno, error) {
    if (error && isStorageError(error)) {
      handleStorageError(error, { showNotification: true, autoReload: true })
      return true // Prevent default error handling
    }
    
    // Call original handler
    if (originalErrorHandler) {
      return originalErrorHandler(message, source, lineno, colno, error)
    }
    return false
  }
  
  // Handle unhandled promise rejections
  const originalRejectionHandler = window.onunhandledrejection
  window.onunhandledrejection = function(event) {
    if (event.reason && isStorageError(event.reason)) {
      handleStorageError(event.reason, { showNotification: true, autoReload: true })
      event.preventDefault() // Prevent default rejection handling
      return
    }
    
    // Call original handler
    if (originalRejectionHandler) {
      originalRejectionHandler.call(window, event)
    }
  }
}

/**
 * Wrap an async function with storage error handling
 */
// `any[]` here is the idiomatic constraint for "any async function"; `unknown[]`
// would reject every concrete signature. This is the one place it stays.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function withStorageErrorHandling<T extends (...args: any[]) => Promise<any>>(
  fn: T,
  options?: Parameters<typeof handleStorageError>[1]
): T {
  return (async (...args: Parameters<T>) => {
    try {
      return await fn(...args)
    } catch (error) {
      if (error instanceof Error && isStorageError(error)) {
        await handleStorageError(error, options)
        throw error // Re-throw after handling
      }
      throw error // Not a storage error, re-throw as-is
    }
  }) as T
}

/**
 * Get storage usage info (for debugging)
 */
export async function getStorageInfo(): Promise<{
  quota: number
  usage: number
  percentage: number
  available: number
}> {
  if ('storage' in navigator && 'estimate' in navigator.storage) {
    try {
      const estimate = await navigator.storage.estimate()
      const quota = estimate.quota || 0
      const usage = estimate.usage || 0
      const percentage = quota > 0 ? (usage / quota) * 100 : 0
      const available = quota - usage
      
      return { quota, usage, percentage, available }
    } catch {
      // Failed to get storage estimate
    }
  }
  
  return { quota: 0, usage: 0, percentage: 0, available: 0 }
}

/**
 * Monitor storage usage and warn if getting full
 */
export async function monitorStorage(thresholdPercentage: number = 80): Promise<void> {
  const info = await getStorageInfo()
  
  if (info.percentage > thresholdPercentage) {
    toast.error(
      `Browser storage is ${info.percentage.toFixed(0)}% full. Consider clearing cache.`,
      { duration: 5000 }
    )
  }
}

